"use client";

import { useCallback, useEffect, useRef } from "react";
import { useAuthStore } from "@/stores/auth-store";

/**
 * Component that handles mobile app state changes (Capacitor)
 * Ensures sessions persist when app goes to background/foreground
 * 
 * CRITICAL: This prevents session loss when:
 * - App is backgrounded (iOS/Android)
 * - App is closed and reopened
 * - Device goes to sleep
 */
export function MobileSessionHandler() {
  const syncSession = useAuthStore((state) => state.syncSession);
  const inFlightSyncRef = useRef<Promise<void> | null>(null);
  const lastSyncAtRef = useRef(0);

  const requestSync = useCallback(async () => {
    const now = Date.now();
    const MIN_SYNC_INTERVAL_MS = 3000;

    if (inFlightSyncRef.current) {
      await inFlightSyncRef.current;
      return;
    }

    if (now - lastSyncAtRef.current < MIN_SYNC_INTERVAL_MS) {
      return;
    }

    const syncPromise = syncSession()
      .catch((error) => {
        console.warn("[MobileSessionHandler] Error syncing session:", error);
      })
      .finally(() => {
        inFlightSyncRef.current = null;
      });

    inFlightSyncRef.current = syncPromise;
    lastSyncAtRef.current = now;
    await syncPromise;
  }, [syncSession]);

  useEffect(() => {
    // Only run in browser/mobile app context
    if (typeof window === "undefined") return;

    // Check if we're in a Capacitor app
    const isCapacitor = (window as any).Capacitor !== undefined;

    // On desktop web, skip focus/visibility sync - it fires during login and can conflict
    // with setSession during the auth storm. Capacitor needs it for app resume.
    const skipFocusVisibility = !isCapacitor;
    
    if (isCapacitor) {
      const { App } = (window as any).Capacitor.Plugins;
      
      if (App) {
        // Listen for app state changes
        const listener = App.addListener("appStateChange", async (state: { isActive: boolean }) => {
          console.log("[MobileSessionHandler] App state changed:", state);
          
          // When app comes to foreground, sync session to ensure it's fresh
          if (state.isActive) {
            console.log("[MobileSessionHandler] App came to foreground, syncing session");
            await requestSync();
          }
        });

        return () => {
          listener.remove();
        };
      }
    }

    if (skipFocusVisibility) {
      return;
    }

    // Capacitor-only fallback: Use page visibility API for web browsers in app
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        console.log("[MobileSessionHandler] Page became visible, syncing session");
        await requestSync();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleFocus = async () => {
      console.log("[MobileSessionHandler] Window focused, syncing session");
      await requestSync();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [requestSync]);

  return null; // This component doesn't render anything
}

